
/// <reference path="../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../frontend/dispatcher';
import { TaskListResultStatus, TaskList } from './TaskList';
import { ITranscription, ITranscriptionSegment } from './interfaces/ITranscriptionManager';
import { IAuthenticationProvider } from './interfaces/IAuthenticationProvider';
import * as DocumentStorage from './office-js/DocumentStorage';
import { IOneDriveUploadTaskResult, OneDriveUploadTask, OneDriveUploadTaskResultStatus } from './OneDriveUploadTask';
import { TranscriptionStorageType, IStoredTranscription } from './interfaces/IStoredTranscription';
import { LoadingActionTypes, LoadingStatus, LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage } from '../frontend/actions/LoadingActions';
import { IForegroundTask } from './interfaces/IForegroundTask';
import { LogEvents } from '../utils/LogEvents';
import { ErrorStringFromException, wait } from '../utils/utils';
import { TranscriptionMonthlyQuota } from './TranscriptionMonthlyQuota';
import { CreateTranscriptionSourceFileProvider } from './TranscriptionSourceFileFactory';
import { ITranscriptionSourceFileProvider } from './interfaces/ITranscriptionSourceFileProvider';
import { FileUploadKeys } from './telemetry/FileUploadKeys';
import { DurationMeasureType } from './interfaces/ILogEvent';
import { LogError, LogErrorAndThrow, ShouldLogErrorAsWarning } from '../utils/ErrorTelemetry';
import { LogEventNames } from '../backend/interfaces/ITelemetryLogger';
import { LogServiceEvent } from '../backend/TelemetryLogger/LogServiceEvent';
import { FileUploadForegroundTaskError, FileUploadForegroundTaskErrors } from './FileUploadForegroundTaskErrors';
import { FrontDoorFileUpload, ConversationId, FrontDoorUploadResult } from './FrontDoorFileUpload';
import { FrontDoorFileUploadError, FrontDoorFileUploadErrors } from './FrontDoorFileUploadErrors';
import { BatchTranscriptionClientRequestPoller, IBatchTranscriptionClientResult, initializeBatchTranscriptionClient, uninitializeBatchTranscriptionClient, BatchTranscriptionClientError, BatchTranscriptionClientErrorCode } from '@1js/al-voice-client';
import { EnsureAugloopRuntimeManager, GetAugloopEventEmitter } from './augloop/Augloop';
import { IAugLoopRuntimeManager } from '@augloop/office-online';
import { secFrom100nsec, secFromMs, GetNumericSpeakerIdFromSpeakerId, RetryServiceCallUntilSuccessOrTimeout } from './Utils';
import { v4 as uuidv4 } from 'uuid';
import { AugloopEvent } from './interfaces/IAugloopEventEmitter';
import { CheckNetworkConnection, GetNetworkConnectionMonitorInstance, NetworkConnectionMonitorConfig, IsConnectedToNetwork } from '../frontend/NetworkConnectionMonitor';
import { NetworkError, NetworkErrors } from '../utils/Errors';
import { TranscriptionRoutePathNames } from '../frontend/routes';
import { GetDefaultTranscriptionLocale, GetLanguageFromLocale } from '../utils/TranscriptionLanguages';
import { GET_BATCH_CONVERSATION_TRANSCRIPTION_TIMEOUT_MILLISECONDS, FILE_UPLOAD_POLLING_DELAY_MILLISECONDS, FILE_UPLOAD_MAX_FILE_SIZE_BYTES } from '../AppConstants';
import { GetDelayTimeInMilliSecondForOneDriveUploadTask, GetDelayTimeInMilliSecondForBatchTranscriptionTask } from './FileUploadForegroundTaskUtil';
import { CreateTranscriptionManager } from './TranscriptionManagerFactory'
import { IAnnotationObserver } from '@1js/al-voice-client';
import { HostAppSettingName, HostAppSettings } from './office-js/HostAppSettings';
import { IOneDriveItemInfo } from './interfaces/IOneDriveItem';
import { MakeOneDriveItemGraphRequest } from './ms-graph/GraphApiCalls';
import { Client } from '@microsoft/microsoft-graph-client';
import * as GraphClientInitOptions from './ms-graph/GraphClientInitOptions';
import * as parsing from './DocumentStorageTranscriptionManagerParsing';
import { IsFileFormatSupportedForUpload, IsFileContentTypeSupportedForUpload } from '../utils/FileUploadSupportedFormat';
import { TranscriptionTelemetryErrorCodes } from '../utils/TranscriptionTelemetryErrorCodes';
import { IsFileNameValidForOneDriveUpload } from '../utils/utils';

function SetLoadingPercentDone(percentDone: number | null) {
    Dispatcher.dispatch({
        type: LoadingActionTypes.SetPercentDoneAction,
        payload: percentDone
    });
}

function SetLoadingStatus(status: LoadingStatus) {
    Dispatcher.dispatch({
        type: LoadingActionTypes.SetStatusAction,
        payload: status
    });
}

type BatchTranscriptionUploadTaskListTypes = void | FrontDoorUploadResult | ITranscription | IOneDriveItemInfo;
type StoredTranscriptionAndSourceFileProvider = { storedTranscription: IStoredTranscription, sourceFileProvider: ITranscriptionSourceFileProvider };

// Rescale a percentage in range [0, 1] to [start, end].
// i.e. 0 will map to start, 1 will map to end and, anything in between will be scaled.
function RescalePercent(percent: number, start: number, end: number) {
    return percent * (end - start) + start;
}

// This class contains the logic we execute when the Loading page is shown to the user in the File Upload scenario.
export class FileUploadForegroundTask implements IForegroundTask {
    private file: File;
    private authProvider: IAuthenticationProvider;
    private history: any;
    private taskList: TaskList<any> | null = null;
    private batchTranscriptionUploadTaskList: TaskList<BatchTranscriptionUploadTaskListTypes> | null = null;
    private oneDriveUploadTask: OneDriveUploadTask | null = null;
    private oneDriveUploadResult: IOneDriveUploadTaskResult | null = null;
    private preliminaryFileDurationSeconds: number | null = null; // File duration provided by the browser, used for displaying progress bar
    private serviceFileDurationSeconds: number | null = null; // File duration provided by the service (FrontDoor), used for monthly quota.
    private frontDoorUpload: FrontDoorFileUpload | null = null;
    private uploadDoneTime: number | null = null;
    private transcriptionUploadProgressInterval: number | null = null;
    private oneDriveUploadPromise: Promise<IOneDriveUploadTaskResult> | null = null;
    private batchTranscriptionClientRequestPoller: BatchTranscriptionClientRequestPoller | null = null;
    private batchTranscriptionClientInitPromise: Promise<void> | null = null;
    private alRuntimeManager: IAugLoopRuntimeManager | null = null;
    private removeBatchTranscriptionEventHandlers: null | (() => void) = null;
    private annotationObserver: IAnnotationObserver | undefined;
    private fileUploadTaskProgressPercentageTimer: number | null = null;

    private readonly transcriptionMonthlyQuotaWaitTime: number = 60000;

    // Upload percentage ranges
    private readonly initializeUploadPercentageRange: [number, number] = [0, 0.05];
    private readonly uploadToFrontDoorPercentageRange: [number, number] = [0.05, 0.20];
    private readonly waitForTranscriptPercentageRange: [number, number] = [0.20, 0.93];
    private readonly initializeBatchTranscriptionPercentageRange: [number, number] = [0.93, 0.94];
    private readonly getBatchTranscriptionResultPercentageRange: [number, number] = [0.94, 0.95];
    private readonly uploadToOneDrivePercentageRange: [number, number] = [0.95, 0.97];
    private readonly saveStoredTranscriptionPercentage: [number, number] = [0.97, 0.98];
    private readonly finishUploadPercentageRange: [number, number] = [0.98, 1];

    private readonly fileUploadWithUrlPercentagesRanges = {
      initialPercentage: [0, 0.01],
      fileUploadToOneDrive: [0.02, 0.43],
      analyzeAudioAtFrontDoor: [0.44, 0.45],
      batchTranscriptionClientInitialized: [0.46],
      getBatchTranscriptionResult: [0.47, 0.95],
      saveTranscriptionToDocStorage: [0.96, 0.96],
      updateUploadMinutesToHost: [0.97, 0.99],
      finishUpload: [1]
    };

    constructor(file: File, authProvider: IAuthenticationProvider, history: any) {
      this.file = file;
      this.authProvider = authProvider;
      this.history = history;
      LogEvents.FileUpload.Start();
      LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.TotalUploadDuration);
      if (HostAppSettings.IsSettingTrue(HostAppSettingName.EnableSetAnnotationsFromALVoiceClient)){
        this.annotationObserver = {
          onAnnotationResult: (op) => {
            if (this.batchTranscriptionClientRequestPoller) {
              this.batchTranscriptionClientRequestPoller.onAnnotationResult(op);
            } else {
              LogError(LogEventNames.FileUpload, "batchTranscriptionClientRequestPoller is null");
            }
          },
        };
      }
    }

    private CleanUpTaskState() {
        this.clearFileUploadTaskProgressPercentageTimer();
        this.taskList = null;
        this.batchTranscriptionUploadTaskList = null;
        this.oneDriveUploadTask = null;
        this.frontDoorUpload = null;
        this.transcriptionUploadProgressInterval = null;
        this.oneDriveUploadResult = null;
        GetNetworkConnectionMonitorInstance().SetConfig(NetworkConnectionMonitorConfig.ShowTransientError, this.history);
    }

    private StartFileUploadTaskProgressPercentageTimer(startPercentage: number, endPercentage: number, delay: number) {
      let currentPercentage = startPercentage;

      let timer = window.setInterval(() => {
        if (currentPercentage + 0.01 < endPercentage) {
          currentPercentage = currentPercentage + 0.01; // 1 % increment with each time delay interval.
          this.TrySetLoadingPercentDone(currentPercentage);
        }
      }, delay);
      this.fileUploadTaskProgressPercentageTimer = timer;
    }

    private clearFileUploadTaskProgressPercentageTimer() {
      if (this.fileUploadTaskProgressPercentageTimer != null) {
        window.clearInterval(this.fileUploadTaskProgressPercentageTimer);
        this.fileUploadTaskProgressPercentageTimer = null;
      }
    }

    public async Execute(): Promise<void> {
      try {
          const isNewDesign = HostAppSettings.IsSettingTrue(HostAppSettingName.FileUploadNewDesign);
          if (!isNewDesign) {
            // Show Transcribing status and 0% progress indicator
            SetLoadingStatus(LoadingStatus.TranscribingAudio);
          }

          GetNetworkConnectionMonitorInstance().SetConfig(NetworkConnectionMonitorConfig.ShowTerminalError, this.history);

          // Default to indeterminate until we figure out if we can get a preliminary audio length.
          SetLoadingPercentDone(null);

          if (isNewDesign) {
              this.taskList = this.GetFileUploadWithUrlTaskList();
          } else {
              this.taskList = this.GetFileUploadTaskList();
          }
          const result = await this.taskList.ExecuteTaskListSequentially();

          this.CleanUpTaskState();

          // If the upload was canceled, we are done.
          if (result.status == TaskListResultStatus.Canceled) {
            LogEvents.FileUpload.End();
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: 'User canceled', isFinalResponse: true });
            return;
          }

          // Only navigate to transcribing page if we aren't already on the failure page
          // Being on the failure page signifies an incomplete transcript/other error that we already logged by navigating to the failure page
          // Swallowing that error can give the user an impression that the transcription experience is inconsistent - especially if they never saw the error in the first place
          if (this.history.location.pathname != TranscriptionRoutePathNames.Failure) {
            await LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(result.result, this.history);
          }

          // Show 100% progress indicator
          this.TrySetLoadingPercentDone(this.finishUploadPercentageRange[1]);

          LogEvents.FileUpload.End();
      } catch (e: any) {
          await this.Cancel();

          if (!IsConnectedToNetwork()) {
            LogError(LogEventNames.FileUpload, "Error uploading file. " + TranscriptionTelemetryErrorCodes.ConnectionIssueFailureReason);
          }

          if (ShouldLogErrorAsWarning(e)) {
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: 'Cancelled because of a warning: ' + ErrorStringFromException(e), isFinalResponse: true });
            LogEvents.FileUpload.End();
          } else {
            const failureReason = "Error uploading file. " + ErrorStringFromException(e);
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: failureReason, isFinalResponse: true });
            LogEvents.FileUpload.End([{ key: FileUploadKeys.FailureReason, value: failureReason }]);
          }

          LogErrorAndThrow(ShouldLogErrorAsWarning(e) ? LogEventNames.Warning : LogEventNames.FileUpload, e);
      }
    }

    public async Cancel(): Promise<void> {
        try {
            if (this.taskList != null)
                this.taskList.Cancel();

            if (this.oneDriveUploadTask != null)
                await this.oneDriveUploadTask.Cancel();

            if (this.batchTranscriptionUploadTaskList != null)
                this.batchTranscriptionUploadTaskList.Cancel();

            if (this.frontDoorUpload != null) {
                this.frontDoorUpload.Cancel();
            }

            if (this.oneDriveUploadResult != null){
                this.oneDriveUploadResult.status = OneDriveUploadTaskResultStatus.Canceled;
            }

            if (this.transcriptionUploadProgressInterval != null) {
                clearTimeout(this.transcriptionUploadProgressInterval);
            }

            if (this.removeBatchTranscriptionEventHandlers != null)
                this.removeBatchTranscriptionEventHandlers();

            if (this.batchTranscriptionClientRequestPoller != null)
                this.DisposeBatchTranscriptionClientRequestPoller();

            uninitializeBatchTranscriptionClient(this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */);

            this.CleanUpTaskState();
        } catch (e) {
            this.CleanUpTaskState();
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: false, message: `Failed when cancelling foreground task: ${ErrorStringFromException(e)}`, isFinalResponse: true });
            LogErrorAndThrow(LogEventNames.FileUpload, e);
        }
    }

    private async AddUploadMinutesToHost() {
        let audioDuration = null;

        if (this.serviceFileDurationSeconds == null)
            throw new Error("Reached AddUploadMinutesToHost without serviceFileDurationSeconds");

        audioDuration = this.serviceFileDurationSeconds;

        let didAddUploadMinutes: boolean = false;
        LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.AddMinutesUsedDuration);
        const addMinutesUsedPromise = TranscriptionMonthlyQuota.GetInstance().AddMinutesUsed(Math.floor(audioDuration / 60)).then(() => {
            didAddUploadMinutes = true;
        });

        await Promise.race([wait(this.transcriptionMonthlyQuotaWaitTime), addMinutesUsedPromise]).then(() => {
            if (!didAddUploadMinutes)
                LogError(LogEventNames.FileUpload, "AddMinutesUsed promise timed out");
        });

        LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.AddMinutesUsedDuration);
    }

    private GetEstimatedTranscriptionPercent(timeFromUploadDone: number) {
        if (this.preliminaryFileDurationSeconds == null) {
            throw new Error("Preliminary file duration seconds is not initialized");
        }

        const maxAudioLength = 300;
        const minTranscriptionTimeAsPercentageOfAudioLength = 0.05;
        const maxTranscriptionTimeAsPercentageOfAudioLength = 0.15;
        let transcriptionTimeAsPercentageOfAudioLength;

        // If the file is more than 5 minutes long, assume it will take 5% of audio length
        if (this.preliminaryFileDurationSeconds > maxAudioLength) {
            transcriptionTimeAsPercentageOfAudioLength = minTranscriptionTimeAsPercentageOfAudioLength;
        } else { // Otherwise assume it will take somewhere between 15% and 5% (map audio length time to this range, e.g. 0 = 15% and 5 minutes = 5%)
            transcriptionTimeAsPercentageOfAudioLength = maxTranscriptionTimeAsPercentageOfAudioLength - (this.preliminaryFileDurationSeconds / maxAudioLength) * (maxTranscriptionTimeAsPercentageOfAudioLength - minTranscriptionTimeAsPercentageOfAudioLength);
        }

        LogEvents.FileUpload.AddData([{ key: FileUploadKeys.EstimatedTranscriptionTimeAsPercentageOfAudioLength, value: transcriptionTimeAsPercentageOfAudioLength }]);
        return Math.min((secFromMs(timeFromUploadDone) * (1/transcriptionTimeAsPercentageOfAudioLength) / this.preliminaryFileDurationSeconds), 1)
    }

    private ReportTranscriptionUploadProgress(lastTranscriptionUploadPercent: number) {
        if (this.uploadDoneTime == null) {
            throw new Error("Upload done time is not initialized");
        }

        const timeFromUploadDone = Date.now() - this.uploadDoneTime;
        const estimatedPercent = this.GetEstimatedTranscriptionPercent(timeFromUploadDone);

        const percentReported = Math.max(lastTranscriptionUploadPercent, RescalePercent(estimatedPercent, this.waitForTranscriptPercentageRange[0], this.waitForTranscriptPercentageRange[1]));
        this.TrySetLoadingPercentDone(percentReported);
        return percentReported;
    }

    private async MakeBatchTranscriptionRequest(conversationId: ConversationId, speechFDUrl: string): Promise<IBatchTranscriptionClientResult> {
        if (this.alRuntimeManager == null)
            throw new Error("alRuntimeManager is not initialized");

        if (this.batchTranscriptionClientRequestPoller != null)
            throw new Error("batchTranscriptionClientRequestPoller already initialized. Dispose it first.");

        this.batchTranscriptionClientRequestPoller = new BatchTranscriptionClientRequestPoller(FILE_UPLOAD_POLLING_DELAY_MILLISECONDS, this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */, conversationId, speechFDUrl);

        if (!(HostAppSettings.IsSettingTrue(HostAppSettingName.EnableSetAnnotationsFromALVoiceClient))) {
          // We need to set the callback where all annotations will come to and forward them to our speech to text client.
          this.alRuntimeManager.setAnnotationResultCallback({
              handler: (op) => {
                this.batchTranscriptionClientRequestPoller!.onAnnotationResult(op);
              }
          });
        }
        return this.batchTranscriptionClientRequestPoller.makeRequest();
    }

    private DisposeBatchTranscriptionClientRequestPoller() {
        if (this.batchTranscriptionClientRequestPoller == null)
            throw new Error("Attempted to dispose a null batchTranscriptionClientRequest");

        this.batchTranscriptionClientRequestPoller.dispose();
        this.batchTranscriptionClientRequestPoller = null;
    }

    private async InitializeBatchTranscriptionClient() {
        if (this.alRuntimeManager == null) {
            throw new Error("alRuntime manager is not initialized");
        }

        const logServiceEventInitialize = new LogServiceEvent(LogEventNames.ServiceBatchTranscriptionInitialize);
        logServiceEventInitialize.Start();
        const enableSetAnnotationsFromALVoiceClient = HostAppSettings.IsSettingTrue(HostAppSettingName.EnableSetAnnotationsFromALVoiceClient);
        if (enableSetAnnotationsFromALVoiceClient) {
          this.batchTranscriptionClientInitPromise = RetryServiceCallUntilSuccessOrTimeout<void>(logServiceEventInitialize, () => initializeBatchTranscriptionClient(this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */, this.annotationObserver, enableSetAnnotationsFromALVoiceClient), false /*shouldRetryOnNull*/);
        } else {
          this.batchTranscriptionClientInitPromise = RetryServiceCallUntilSuccessOrTimeout<void>(logServiceEventInitialize, () => initializeBatchTranscriptionClient(this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */), false /*shouldRetryOnNull*/);
        }
    }

    private TrySetLoadingPercentDone(percentDone: number) {
        // If we don't have a preliminary file duration, we cannot estimate percent done.
        const isNewDesign = HostAppSettings.IsSettingTrue(HostAppSettingName.FileUploadNewDesign);
        if (!isNewDesign && this.preliminaryFileDurationSeconds == null)
            return

        SetLoadingPercentDone(percentDone);
    }

    // If batch transcription client has not been initialized, do it.
    private async EnsureBatchTranscriptionClientInitialized(): Promise<void> {
        if (this.batchTranscriptionClientInitPromise != null)
            return this.batchTranscriptionClientInitPromise;

        const onSessionConnectAfterCloseListener = () => {
            this.InitializeBatchTranscriptionClient();
        }

        const onSessionCloseListener = () => {
            GetAugloopEventEmitter().once(AugloopEvent.SessionConnect, onSessionConnectAfterCloseListener);
        }

        // If the augloop session is closed and we reconnect, re-initialize batch transcription client.
        GetAugloopEventEmitter().on(AugloopEvent.SessionClose, onSessionCloseListener);

        this.removeBatchTranscriptionEventHandlers = () => {
            GetAugloopEventEmitter().removeListener(AugloopEvent.SessionClose, onSessionCloseListener);
            GetAugloopEventEmitter().removeListener(AugloopEvent.SessionConnect, onSessionConnectAfterCloseListener);
            this.removeBatchTranscriptionEventHandlers = null;
        }

        this.InitializeBatchTranscriptionClient();

        if (this.batchTranscriptionClientInitPromise == null)
            throw new Error("Failed creating batch transcripting client");

        return this.batchTranscriptionClientInitPromise;
    }

    private GetBatchTranscriptionUploadTaskList(): TaskList<BatchTranscriptionUploadTaskListTypes> {
        const locale = GetDefaultTranscriptionLocale();
        let fileSizeUploadMaxFileSize = HostAppSettings.GetSettingAsNumber(HostAppSettingName.FileSizeUploadLimit);
        if (fileSizeUploadMaxFileSize <= 0 || fileSizeUploadMaxFileSize == null || fileSizeUploadMaxFileSize == undefined) {
            fileSizeUploadMaxFileSize = FILE_UPLOAD_MAX_FILE_SIZE_BYTES;
        }
        return new TaskList<BatchTranscriptionUploadTaskListTypes>([
            // Check file size, file name and get audio duration
            async () => {
                if (this.file.size > fileSizeUploadMaxFileSize) {
                    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileExceedsSizeLimit);
                }

                if (!IsFileNameValidForOneDriveUpload(this.file.name)) {
                    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.InvalidFileNameForOneDriveFileUpload);
                }

                try {
                    this.preliminaryFileDurationSeconds = await GetAudioFileDuration(this.file);
                    LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioLengthSeconds, value: this.preliminaryFileDurationSeconds}]);
                    this.TrySetLoadingPercentDone(this.initializeUploadPercentageRange[1]);
                } catch (e) {
                    LogEvents.FileUpload.AddData([{ key: FileUploadKeys.PreeliminaryAudioLengthUnavailable, value: false }]);
                }
            },

            // Get runtime manager
            async () => {
                this.alRuntimeManager = await EnsureAugloopRuntimeManager();
            },

            // Upload the file to FrontDoor
            async (): Promise<FrontDoorUploadResult> => {
                const sessionId = uuidv4();
                LogEvents.FileUpload.AddData([{ key: FileUploadKeys.FrontDoorSessionId, value: sessionId }]);
                console.log(`Transcription FrontDoor upload session ID: ${sessionId}`);
                this.frontDoorUpload = new FrontDoorFileUpload();

                let lastTranscriptionUploadPercent = 0;

                try {
                    LogEvents.FileUpload.AddData([{ key: FileUploadKeys.TranscriptionLocale, value: locale }]);

                    const result = await this.frontDoorUpload.AnalyzeAudio(sessionId, this.file, false /*isNewDesign*/, '' /*fileDownloadUrl*/, ''/*fileContentType*/, (percent: number) => {
                        this.TrySetLoadingPercentDone(RescalePercent(percent, this.uploadToFrontDoorPercentageRange[0], this.uploadToFrontDoorPercentageRange[1]));
                    } /*onUploadProgress*/, async () => {
                        this.uploadDoneTime = Date.now();

                        if (this.oneDriveUploadTask == null && this.oneDriveUploadPromise == null) {
                            // Trigger OneDrive upload as soon as we are done uploading to FrontDoor.
                            this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, TranscriptionStrings.FolderNameToUploadAudioFiles, this.file);
                            this.oneDriveUploadPromise = this.oneDriveUploadTask.UploadFile();
                        }

                        // Kick off fake upload progress bar if we have a preliminary file duration.
                        if (this.preliminaryFileDurationSeconds != null) {
                            // It is possible that the interval was previously started because of retries.
                            if (this.transcriptionUploadProgressInterval == null) {
                                this.transcriptionUploadProgressInterval = window.setInterval(() => {
                                    lastTranscriptionUploadPercent = this.ReportTranscriptionUploadProgress(lastTranscriptionUploadPercent)
                                }, 300);
                            }
                        }

                        await this.EnsureBatchTranscriptionClientInitialized();
                    } /*onUploadDone*/, locale);

                    this.serviceFileDurationSeconds = secFromMs(result.audioLengthInMS);
                    LogEvents.FileUpload.AddData([{ key: FileUploadKeys.ServiceFileDurationSeconds, value: this.serviceFileDurationSeconds}]);

                    if (this.transcriptionUploadProgressInterval != null) {
                        window.clearInterval(this.transcriptionUploadProgressInterval);
                        this.transcriptionUploadProgressInterval = null;
                    }

                    this.TrySetLoadingPercentDone(this.waitForTranscriptPercentageRange[1]);
                    return result;
                } catch (e) {
                    if (e instanceof FrontDoorFileUploadError) {
                        throw PublicErrorFromFrontDoorUploadError(e);
                    }

                    throw e;
                }
            },

            async (frontDoorUploadResult: FrontDoorUploadResult): Promise<FrontDoorUploadResult> => {
                LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: "Successfully upload file to FD", isFinalResponse: true });
                this.TrySetLoadingPercentDone(this.initializeBatchTranscriptionPercentageRange[0]);
                await this.EnsureBatchTranscriptionClientInitialized()
                return frontDoorUploadResult;
            },

            // Get batch result
            async (frontDoorUploadResult: FrontDoorUploadResult): Promise<ITranscription> => {
                this.TrySetLoadingPercentDone(this.getBatchTranscriptionResultPercentageRange[0]);

                const logServiceEventGetResult = new LogServiceEvent(LogEventNames.ServiceBatchTranscriptionGetResult);
                logServiceEventGetResult.Start();
                let batchTranscriptionResult = null;

                try {
                  batchTranscriptionResult = await RetryServiceCallUntilSuccessOrTimeout<Promise<IBatchTranscriptionClientResult>>(logServiceEventGetResult, async () => {
                      return new Promise(async (resolve, reject) => {
                          // If this is not the first request (i.e. this is a retry), dispose the previous one.
                          if (this.batchTranscriptionClientRequestPoller != null)
                              this.DisposeBatchTranscriptionClientRequestPoller();

                          try {
                              resolve(await this.MakeBatchTranscriptionRequest(frontDoorUploadResult.conversationId, frontDoorUploadResult.speechFDUrl));
                          } catch (e: any) {
                              // When we dispose a request, this exception will be thrown if the request is pending. Ignore it.
                              if (e !instanceof BatchTranscriptionClientError || e.code != BatchTranscriptionClientErrorCode.REQUEST_DISPOSED) {
                                  reject(e);
                              }
                          }
                      });
                  }, false /*shouldRetryOnNull*/, this.ShouldRetryBatchTranscriptionError, () => false, GET_BATCH_CONVERSATION_TRANSCRIPTION_TIMEOUT_MILLISECONDS);
               } catch (e : any) {
                 switch ( e.code ) {
                   case BatchTranscriptionClientErrorCode.FILE_DECODE_ERROR :
                      throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileDecodeError, e.message);
                   case  BatchTranscriptionClientErrorCode.FILE_NOT_EXIST :
                      throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileNotExist, e.message);
                   case  BatchTranscriptionClientErrorCode.DELETED :
                      throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.DeletedTranscript, e.message);
                   default :
                      throw e;  
                }
              }

              this.batchTranscriptionClientRequestPoller!.dispose();
              uninitializeBatchTranscriptionClient(this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */);

              if (this.removeBatchTranscriptionEventHandlers != null)
                  this.removeBatchTranscriptionEventHandlers();

              this.TrySetLoadingPercentDone(this.getBatchTranscriptionResultPercentageRange[1]);

              const newAudioCalculationImpl = HostAppSettings.IsSettingTrue(HostAppSettingName.FileUploadNewDurationImpl);
              if(newAudioCalculationImpl) {
                  const transcript = GetTranscriptionFromBatchTranscriptionResult(batchTranscriptionResult, locale);
                  if (transcript != null && transcript.transcriptSegments.length > 0) {
                      let last = transcript.transcriptSegments[transcript.transcriptSegments.length - 1];
                      const time: number = last.end;
                      this.serviceFileDurationSeconds = time;
                  }
                  return transcript;
              }
              else {
                  return GetTranscriptionFromBatchTranscriptionResult(batchTranscriptionResult, locale);
              }
            },
        ]);
    }

    private ShouldRetryBatchTranscriptionError(error: BatchTranscriptionClientError) {
      return (typeof error !== "undefined" && typeof error.code !== "undefined"
        && !(error.code == BatchTranscriptionClientErrorCode.DELETED
          || error.code == BatchTranscriptionClientErrorCode.FILE_NOT_EXIST
          || error.code == BatchTranscriptionClientErrorCode.FILE_DECODE_ERROR));
    }

    // Executes the tasks required for the file upload scenario.
    private GetFileUploadTaskList(): TaskList<any> {
        return new TaskList< void | ITranscription | IStoredTranscription | StoredTranscriptionAndSourceFileProvider | ITranscriptionSourceFileProvider >([
            // Tasks to transcribe file --->
            //  1. Do the upload
            async (): Promise<ITranscription> => {
                const fileExtension: string | undefined = this.file.name.split('.').pop();
                if (fileExtension) {
                    LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioFileExtension, value: fileExtension}]);
                }

                LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioFileSizeBytes, value: this.file.size}]);
                LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.TranscriptionProcessDuration);

                let transcription = null;
                CheckNetworkConnection();
                this.batchTranscriptionUploadTaskList = this.GetBatchTranscriptionUploadTaskList();
                const fastUploadTaskListResult = await this.batchTranscriptionUploadTaskList.ExecuteTaskListSequentially();
                transcription = <ITranscription>fastUploadTaskListResult.result;

                LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.TranscriptionProcessDuration);
                LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: '', isFinalResponse: true});
                return transcription;
            },

            // <--- Transcribing file ends

            // Upload the file to OneDrive and create transcription manager.
            async (transcription: ITranscription): Promise<IStoredTranscription> => {
                // Show 95% progress indicator
                this.TrySetLoadingPercentDone(this.uploadToOneDrivePercentageRange[0]);

                // change status to uploading file
                SetLoadingStatus(LoadingStatus.UploadingFile);

                if (this.oneDriveUploadPromise == null) {
                    throw new Error("oneDriveUploadPromise is null");
                }

                const uploadResult: IOneDriveUploadTaskResult = await this.oneDriveUploadPromise;

                if (uploadResult == null) {
                    throw new Error("UploadFile returned a null uploadResult");
                }

                if (uploadResult.status == OneDriveUploadTaskResultStatus.Canceled) {
                    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.OneDriveUploadCanceled);
                }

                if (uploadResult.oneDriveItem == null) {
                    throw new Error("UploadFile returned a null oneDriveItem");
                }

                return {
                    storageType: TranscriptionStorageType.DocumentStorage,
                    descriptor:  {
                        transcription,
                        audioOneDriveItem: uploadResult.oneDriveItem,
                    }
                };
            },

            // Write stored transcription to document storage.
            async (storedTranscription: IStoredTranscription): Promise<StoredTranscriptionAndSourceFileProvider> => {
                // Show 97% progress indicator
                this.TrySetLoadingPercentDone(this.saveStoredTranscriptionPercentage[0]);

                await DocumentStorage.WriteStoredTranscription(storedTranscription);
                return {
                    storedTranscription,
                    sourceFileProvider: CreateTranscriptionSourceFileProvider(storedTranscription, this.authProvider),
                }
            },

            // Add upload minutes to the host.
            async (storedTranscriptionAndSourceFileProvider: StoredTranscriptionAndSourceFileProvider): Promise<ITranscriptionSourceFileProvider> => {
                // Show 98% progress indicator

                const transcriptionManager = CreateTranscriptionManager(storedTranscriptionAndSourceFileProvider.storedTranscription);
                this.TrySetLoadingPercentDone(this.finishUploadPercentageRange[0]);

                // Only add upload minutes if this transcript has more than zero segments
                if (transcriptionManager.GetTranscription().transcriptSegments.length !== 0)
                    await this.AddUploadMinutesToHost();

                return storedTranscriptionAndSourceFileProvider.sourceFileProvider;
            }
        ]);
    }

    private GetBatchTranscriptionUploadWithUrlTaskList(): TaskList<BatchTranscriptionUploadTaskListTypes> {
      const locale = GetDefaultTranscriptionLocale();
      return new TaskList<BatchTranscriptionUploadTaskListTypes>([
          // Check file validity and log audio duration.
          async () => {
            this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.initialPercentage[1]);
            CheckIfFileValidToUpload(this.file);

            /*Note: adding this as we need to log file meta data( here audio file duration). This is needed for powebi dashboard metrics
            and debugging any customer issues. We will refactor to move this out of this method once we remove the old file upload code*/
            const calculateAndLogAudioFileDurationTaskAsync = async () => {
              try {
                this.preliminaryFileDurationSeconds = await GetAudioFileDuration(this.file);
                LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioLengthSeconds, value: this.preliminaryFileDurationSeconds}]);
               } catch (e) {
                LogEvents.FileUpload.AddData([{ key: FileUploadKeys.PreeliminaryAudioLengthUnavailable, value: false }]);
              }
            }
            calculateAndLogAudioFileDurationTaskAsync();
          },

          // Get runtime manager
          async () => {
              this.alRuntimeManager = await EnsureAugloopRuntimeManager();
          },

          // upload the file to onedrive and get file dowload url
          async () : Promise<IOneDriveItemInfo> => {
              //Show Upload File to onedrive Status.
              SetLoadingStatus(LoadingStatus.UploadingFile);

              // Setting up upload percentage to 2% on file upload start.
              this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.fileUploadToOneDrive[0]);

              //Start the timer.
              let delay = GetDelayTimeInMilliSecondForOneDriveUploadTask(this.file.size);
              this.StartFileUploadTaskProgressPercentageTimer(this.fileUploadWithUrlPercentagesRanges.fileUploadToOneDrive[0], this.fileUploadWithUrlPercentagesRanges.fileUploadToOneDrive[1], delay);

              if (this.oneDriveUploadTask == null && this.oneDriveUploadPromise == null) {
                  this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, TranscriptionStrings.FolderNameToUploadAudioFiles, this.file);
                  this.oneDriveUploadPromise = this.oneDriveUploadTask.UploadFile();
              }
              if (this.oneDriveUploadPromise == null) {
                  throw new Error("oneDriveUploadPromise is null");
              }
              this.oneDriveUploadResult = await this.oneDriveUploadPromise;
              if (this.oneDriveUploadResult == null) {
                  throw new Error("UploadFile returned a null uploadResult");
              }
              if (this.oneDriveUploadResult.status == OneDriveUploadTaskResultStatus.Canceled) {
                  throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.OneDriveUploadCanceled);
              }
              if (this.oneDriveUploadResult.oneDriveItem == null) {
                  throw new Error("UploadFile returned a null oneDriveItem");
              }

              try{
                  const graphClient: Client = Client.initWithMiddleware ({
                      baseUrl: GraphClientInitOptions.GetAuthOptions(this.authProvider).baseUrl,
                      fetchOptions: { cache: 'reload' },
                      middleware: GraphClientInitOptions.GetDefaultMiddlewareChain(this.authProvider, true /*enableTelemetryHandler*/, LogEventNames.ServiceGraphClientOneDriveItem)
                  });
                  const oneDriveItemResponse = await MakeOneDriveItemGraphRequest(graphClient, this.oneDriveUploadResult.oneDriveItem.driveId, this.oneDriveUploadResult.oneDriveItem.itemId);
                  let audioItem = parsing.ParseItemInfoFromGraphResponse(oneDriveItemResponse);
                  this.clearFileUploadTaskProgressPercentageTimer();
                  this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.fileUploadToOneDrive[1]);
                  return audioItem;
              } catch(e){
                  throw new Error("Exception in getting one drive file info/download url : " + ErrorStringFromException(e));
              }
        },

          // Pass download url to FrontDoor to analyze audio
          async (audioItemInfo : IOneDriveItemInfo): Promise<FrontDoorUploadResult> => {
              let downloadUrl: string = '';
              if ((audioItemInfo == null || audioItemInfo.downloadUrl == null) )
                  throw new Error("Didn't get downloadUrl for audio file form Graph API");
              downloadUrl = audioItemInfo.downloadUrl;
              const sessionId = uuidv4();
              LogEvents.FileUpload.AddData([{ key: FileUploadKeys.FrontDoorSessionId, value: sessionId }]);
              console.log(`Transcription FrontDoor upload session ID: ${sessionId}`);
              this.frontDoorUpload = new FrontDoorFileUpload();
              try {
                  LogEvents.FileUpload.AddData([{ key: FileUploadKeys.TranscriptionLocale, value: locale }]);

                  const result = await this.frontDoorUpload.AnalyzeAudio(sessionId, this.file, true /*isNewDesign*/, downloadUrl, GetFileContentTypeFileUpload(this.file), (percent: number) => {
                    this.TrySetLoadingPercentDone(RescalePercent(percent, this.fileUploadWithUrlPercentagesRanges.analyzeAudioAtFrontDoor[0], this.fileUploadWithUrlPercentagesRanges.analyzeAudioAtFrontDoor[1]));
                  } /*onUploadProgress*/, async () => {
                      this.uploadDoneTime = Date.now();

                      if (this.oneDriveUploadTask == null && this.oneDriveUploadPromise == null) {
                          // Trigger OneDrive upload as soon as we are done uploading to FrontDoor.
                          this.oneDriveUploadTask = new OneDriveUploadTask(this.authProvider, TranscriptionStrings.FolderNameToUploadAudioFiles, this.file);
                          this.oneDriveUploadPromise = this.oneDriveUploadTask.UploadFile();
                      }

                      await this.EnsureBatchTranscriptionClientInitialized();
                  } /*onUploadDone*/, locale);

                  // Setting pecentage to 46% on analyze audio on frontdoor completion.
                  this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.analyzeAudioAtFrontDoor[1]);
                  return result;
              } catch (e) {
                  if (e instanceof FrontDoorFileUploadError) {
                      throw PublicErrorFromFrontDoorUploadError(e);
                  }

                  throw e;
              }
          },

          async (frontDoorUploadResult: FrontDoorUploadResult): Promise<FrontDoorUploadResult> => {
              LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: "Successfully send file upload URL to FD", isFinalResponse: true });
              this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.batchTranscriptionClientInitialized[0]);
              await this.EnsureBatchTranscriptionClientInitialized()
              return frontDoorUploadResult;
          },

          // Get batch result
          async (frontDoorUploadResult: FrontDoorUploadResult): Promise<ITranscription> => {
              //Set Loading Status to transcribing audio.
              SetLoadingStatus(LoadingStatus.TranscribingAudio);

              // Setting up upload percentage to 47% on get batch transcription start.
              this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.getBatchTranscriptionResult[0]);

              let delay = GetDelayTimeInMilliSecondForBatchTranscriptionTask(this.file.size);
              this.StartFileUploadTaskProgressPercentageTimer(this.fileUploadWithUrlPercentagesRanges.getBatchTranscriptionResult[0],this.fileUploadWithUrlPercentagesRanges.getBatchTranscriptionResult[1], delay);

              const logServiceEventGetResult = new LogServiceEvent(LogEventNames.ServiceBatchTranscriptionGetResult);
              logServiceEventGetResult.Start();
              let batchTranscriptionResult  = null;
              try {
                batchTranscriptionResult = await RetryServiceCallUntilSuccessOrTimeout<Promise<IBatchTranscriptionClientResult>>(logServiceEventGetResult, async () => {
                    return new Promise(async (resolve, reject) => {
                        // If this is not the first request (i.e. this is a retry), dispose the previous one.
                        if (this.batchTranscriptionClientRequestPoller != null)
                            this.DisposeBatchTranscriptionClientRequestPoller();

                        try {
                            resolve(await this.MakeBatchTranscriptionRequest(frontDoorUploadResult.conversationId, frontDoorUploadResult.speechFDUrl));
                        } catch (e: any) {
                            // When we dispose a request, this exception will be thrown if the request is pending. Ignore it.
                            if (e !instanceof BatchTranscriptionClientError || e.code != BatchTranscriptionClientErrorCode.REQUEST_DISPOSED) {
                                reject(e);
                            }
                        }
                    });
                }, false /*shouldRetryOnNull*/, this.ShouldRetryBatchTranscriptionError, () => false, GET_BATCH_CONVERSATION_TRANSCRIPTION_TIMEOUT_MILLISECONDS);
             } catch (e : any) {
               switch ( e.code ) {
                 case BatchTranscriptionClientErrorCode.FILE_DECODE_ERROR :
                    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileDecodeError, e.message);
                 case  BatchTranscriptionClientErrorCode.FILE_NOT_EXIST :
                    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileNotExist, e.message);
                 case  BatchTranscriptionClientErrorCode.DELETED :
                    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.DeletedTranscript, e.message);
                  default :
                    throw e;  
               }
            }
            this.batchTranscriptionClientRequestPoller!.dispose();
            uninitializeBatchTranscriptionClient(this.alRuntimeManager! as any /* TODO: ADO:4691502 remove cast */);

            if (this.removeBatchTranscriptionEventHandlers != null)
                this.removeBatchTranscriptionEventHandlers();

            this.clearFileUploadTaskProgressPercentageTimer();
            this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.getBatchTranscriptionResult[1]);

            const transcript = GetTranscriptionFromBatchTranscriptionResult(batchTranscriptionResult, locale);
            if (transcript != null && transcript.transcriptSegments.length > 0) {
                let last = transcript.transcriptSegments[transcript.transcriptSegments.length - 1];
                const time: number = last.end;
                this.serviceFileDurationSeconds = time;
                LogEvents.FileUpload.AddData([{ key: FileUploadKeys.ServiceFileDurationSeconds, value: this.serviceFileDurationSeconds}]);
            }
            return transcript;
          },
      ]);
  }

  // Executes the tasks required for the file upload scenario.
  private GetFileUploadWithUrlTaskList(): TaskList<any> {
    return new TaskList< void | ITranscription | IStoredTranscription | StoredTranscriptionAndSourceFileProvider | ITranscriptionSourceFileProvider >([
        // Tasks to transcribe file --->
        //  1. Do the upload
        async (): Promise<ITranscription> => {
            LogEvents.FileUpload.AddData([{ key: FileUploadKeys.IsNewFileUploadDesign, value: true /* file upload new design*/ }]);
            const fileExtension: string | undefined = this.file.name.split('.').pop();
            if (fileExtension) {
                LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioFileExtension, value: fileExtension}]);
            }

            LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioFileMIMEType, value: GetFileContentTypeFileUpload(this.file)}]);
            LogEvents.FileUpload.AddData([{ key: FileUploadKeys.AudioFileSizeBytes, value: this.file.size}]);
            LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.TranscriptionProcessDuration);

            let transcription = null;
            CheckNetworkConnection();
            this.batchTranscriptionUploadTaskList = this.GetBatchTranscriptionUploadWithUrlTaskList();
            const fastUploadTaskListResult = await this.batchTranscriptionUploadTaskList.ExecuteTaskListSequentially();
            transcription = <ITranscription>fastUploadTaskListResult.result;

            LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.TranscriptionProcessDuration);
            LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: '', isFinalResponse: true});
            return transcription;
        },

        // <--- Transcribing file ends

        // Create transcription manager.
        async (transcription: ITranscription): Promise<IStoredTranscription> => {
          if (this.oneDriveUploadResult == null) {
            throw new Error("UploadFile returned a null uploadResult");
          }
            return {
                storageType: TranscriptionStorageType.DocumentStorage,
                descriptor:  {
                    transcription,
                    audioOneDriveItem: this.oneDriveUploadResult.oneDriveItem,
                }
            };
        },

        // Write stored transcription to document storage.
        async (storedTranscription: IStoredTranscription): Promise<StoredTranscriptionAndSourceFileProvider> => {
            // Show 96% progress indicator
            this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.saveTranscriptionToDocStorage[0]);
            await DocumentStorage.WriteStoredTranscription(storedTranscription);
            return {
                storedTranscription,
                sourceFileProvider: CreateTranscriptionSourceFileProvider(storedTranscription, this.authProvider),
            }
        },

        // Add upload minutes to the host.
        async (storedTranscriptionAndSourceFileProvider: StoredTranscriptionAndSourceFileProvider): Promise<ITranscriptionSourceFileProvider> => {
            const transcriptionManager = CreateTranscriptionManager(storedTranscriptionAndSourceFileProvider.storedTranscription);

            // Show 97% progress indicator
            this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.updateUploadMinutesToHost[0]);

            // Only add upload minutes if this transcript has more than zero segments
            if (transcriptionManager.GetTranscription().transcriptSegments.length !== 0)
                await this.AddUploadMinutesToHost();

            // Show 99% progress indicator
            this.TrySetLoadingPercentDone(this.fileUploadWithUrlPercentagesRanges.updateUploadMinutesToHost[1]);
            return storedTranscriptionAndSourceFileProvider.sourceFileProvider;
        }
    ]);
}
}

// Check if file valid for upload
function CheckIfFileValidToUpload(file: File) {
  let fileSizeUploadMaxFileSize = HostAppSettings.GetSettingAsNumber(HostAppSettingName.FileSizeUploadLimit)
  if (fileSizeUploadMaxFileSize <= 0 || fileSizeUploadMaxFileSize == null || fileSizeUploadMaxFileSize == undefined) {
    fileSizeUploadMaxFileSize = FILE_UPLOAD_MAX_FILE_SIZE_BYTES;
  }
  if (file.size <= 0) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.InvalidEmptyFile);
  }

  if (file.size > fileSizeUploadMaxFileSize) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.FileExceedsSizeLimit);
  }

  const fileExtension: string | undefined = file.name.split('.').pop();
  if (!IsFileFormatSupportedForUpload(fileExtension?.toLowerCase())) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.UnsupportedFileFormat);
  }

  if (!IsFileNameValidForOneDriveUpload(file.name)) {
    throw new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.InvalidFileNameForOneDriveFileUpload);
  }
}

// Some browsers (e.g. Safari) do not support the promisified version of decodeAudioData, so this function
// is used to create a promise.
function getDecodeAudioDataPromise(arrayBuffer: ArrayBuffer, audioContext: any): Promise<AudioBuffer> {
    return new Promise((resolve, reject) => {
      audioContext.decodeAudioData(arrayBuffer, resolve, reject);
    });
}

// Gets the duration of the passed audio file
function GetAudioFileDuration(file: File): Promise<number> {
    LogEvents.FileUpload.BeginDurationMeasure(DurationMeasureType.Normal, FileUploadKeys.GetFileDurationDurationSeconds);
    const fileReader = new FileReader();

    const promise = new Promise<number>((resolve, reject) => {
        fileReader.onload = async () => {
        try {
            const packets = <ArrayBuffer>fileReader.result;
            // Create OfflineAudioContext with length 1.
            // This is OfflineAudioContext instead of AudioContext because we don't need to render audio to an output device.
            // Length 1 is enough because we are only using it for audio decoding.
            const audioContext = new OfflineAudioContext(1 /*numberOfChannels*/, 1 /*length*/, 16000 /* sample rate */);
            const audioBuffer: AudioBuffer = await getDecodeAudioDataPromise(packets, audioContext);
            LogEvents.FileUpload.EndDurationMeasure(FileUploadKeys.GetFileDurationDurationSeconds);
            resolve(audioBuffer.duration);
        } catch (e) {
            reject(e);
        }
        };

        fileReader.onabort = fileReader.onerror = () => {
        reject(new Error('fileReader.onerror'));
        };
    });

    fileReader.readAsArrayBuffer(file);
    return promise;
}

function GetTranscriptionFromBatchTranscriptionResult(batchTranscriptionResult: IBatchTranscriptionClientResult, locale: string) {
    const transcript: ITranscription = {
        transcriptSegments: [],
    };

    let maxSpeakerId: number | null = null;

    batchTranscriptionResult.transcriptSegments.forEach((transcriptSegment: any) => {
        const startTimestamp = secFrom100nsec(transcriptSegment.offsetInTicks);
        const endTimestamp = startTimestamp + secFrom100nsec(transcriptSegment.durationInTicks);
        const speakerId = GetNumericSpeakerIdFromSpeakerId(transcriptSegment.userId);

        if (speakerId != null) {
            maxSpeakerId = maxSpeakerId == null ? speakerId : Math.max(speakerId, maxSpeakerId);
        }

        transcript.transcriptSegments.push({
            text: transcriptSegment.text,
            language: GetLanguageFromLocale(locale),
            start: startTimestamp,
            end: endTimestamp,
            speakerId
        });
    });

    transcript.speakerNames = new Array(maxSpeakerId != null ? maxSpeakerId + 1 : 0);

    // We need to sort the segments because speedy CTS may return them out of order due to paralellization on their side.
    transcript.transcriptSegments.sort((segment1: ITranscriptionSegment, segment2: ITranscriptionSegment) => {
        return segment1.start - segment2.start;
    });

    return transcript;
}

// Convert from FrontDoorFileUpload errors to a publicly-facing error
function PublicErrorFromFrontDoorUploadError(e: FrontDoorFileUploadError) {
    switch (e.name) {
        case FrontDoorFileUploadErrors.UnsupportedFileFormat:
            return new FileUploadForegroundTaskError(FileUploadForegroundTaskErrors.UnsupportedFileFormat);

        case FrontDoorFileUploadErrors.NetworkError:
            return new NetworkError(NetworkErrors.NoInternetConnection);

        default:
            return e;
    }
}

function GetFileContentTypeFileUpload(file: File) {
    if (IsFileContentTypeSupportedForUpload(file.type)) {
        return file.type;
    }

    return 'audio/mpeg';
}